// ConfirmDialog (React 18 uyumlu sürüm)
import React from 'react';
import ReactDOM from 'react-dom/client';
import { FaQuestionCircle, FaTrash, FaTimes, FaCheck, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import './ConfirmDialog.css';

// Ana bileşen
const ConfirmDialogComponent = ({ 
  message, 
  onConfirm, 
  onCancel,
  confirmText = 'Evet',
  cancelText = 'Hayır',
  title = 'Onay',
  type = 'warning', // warning, info, error
}) => {
  // ESC tuşu ile iptal
  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  // Türe göre ikon seçimi
  let icon;
  switch (type) {
    case 'error':
      icon = <FaExclamationTriangle />;
      break;
    case 'info':
      icon = <FaInfoCircle />;
      break;
    case 'warning':
    default:
      icon = <FaQuestionCircle />;
      break;
  }

  const [isExiting, setIsExiting] = React.useState(false);

  const handleConfirmClick = () => {
    setIsExiting(true);
    setTimeout(() => {
      onConfirm();
    }, 200);
  };

  const handleCancelClick = () => {
    setIsExiting(true);
    setTimeout(() => {
      onCancel();
    }, 200);
  };

  return (
    <div className={`confirm-dialog-overlay ${isExiting ? 'exiting' : ''}`}>
      <div className={`confirm-dialog ${type} ${isExiting ? 'exiting' : ''}`}>
        <div className="confirm-dialog-header">
          <div className="confirm-dialog-icon">{icon}</div>
          <h3 className="confirm-dialog-title">{title}</h3>
          <button 
            className="confirm-dialog-close" 
            onClick={handleCancelClick}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className="confirm-dialog-content">
          <p className="confirm-dialog-message">{message}</p>
        </div>
        
        <div className="confirm-dialog-actions">
          <button 
            className="confirm-dialog-button cancel"
            onClick={handleCancelClick}
          >
            {cancelText}
          </button>
          <button 
            className="confirm-dialog-button confirm"
            onClick={handleConfirmClick}
            autoFocus
          >
            {type === 'error' ? <FaTrash className="button-icon" /> : <FaCheck className="button-icon" />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

// Dialog container oluşturma ve yönetme
const createConfirmDialog = () => {
  let containerElement = document.getElementById('confirm-dialog-root');
  
  if (!containerElement) {
    containerElement = document.createElement('div');
    containerElement.id = 'confirm-dialog-root';
    document.body.appendChild(containerElement);
  }
  
  const root = ReactDOM.createRoot(containerElement);
  
  return {
    open: (options) => {
      return new Promise((resolve) => {
        const handleConfirm = () => {
          root.render(null);
          resolve(true);
        };
        
        const handleCancel = () => {
          root.render(null);
          resolve(false);
        };
        
        root.render(
          <ConfirmDialogComponent
            {...options}
            onConfirm={handleConfirm}
            onCancel={handleCancel}
          />
        );
      });
    },
    
    close: () => {
      root.render(null);
    }
  };
};

let dialogInstance;

// Singleton instance oluşturma
const getDialogInstance = () => {
  if (!dialogInstance && typeof window !== 'undefined') {
    dialogInstance = createConfirmDialog();
  }
  return dialogInstance;
};

// Dışa aktarılan API
const confirmDialog = {
  // Genel onay dialog'u
  show: async (message, options = {}) => {
    const dialog = getDialogInstance();
    return dialog.open({ message, ...options });
  },
  
  // Uyarı (sarı) dialog'u
  warning: async (message, options = {}) => {
    const dialog = getDialogInstance();
    return dialog.open({ 
      message, 
      type: 'warning',
      title: options.title || 'Uyarı',
      ...options 
    });
  },
  
  // Silme onayı (kırmızı) dialog'u
  delete: async (message, options = {}) => {
    const dialog = getDialogInstance();
    return dialog.open({ 
      message: message || 'Bu öğeyi silmek istediğinizden emin misiniz?', 
      type: 'error',
      title: options.title || 'Silme Onayı',
      confirmText: options.confirmText || 'Sil',
      ...options 
    });
  },
  
  // Bilgi (mavi) dialog'u
  info: async (message, options = {}) => {
    const dialog = getDialogInstance();
    return dialog.open({ 
      message, 
      type: 'info',
      title: options.title || 'Bilgi',
      ...options 
    });
  }
};

export default confirmDialog;